import {
  createSaleFeed,
  type LiveSource,
  type SaleFeed,
  type SaleFeedEvents,
  type SaleFeedStatusInfo,
} from '@auctisync/api-client';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { currentAccessToken } from '../lib/accessToken';
import { useSession } from './useSession';

/** The feed's sale states (`FeedSaleState`), without a direct dependency on the wire package. */
export type FeedSaleState = NonNullable<ReturnType<SaleFeed['saleState']>>;

/**
 * The website's React binding over the shared live feed (`createSaleFeed`,
 * `@auctisync/api-client`) — and NOTHING ELSE. It creates the feed when a
 * sale runs on the engine, keeps it told which lots are on screen and
 * watched, re-renders the page when the feed changes, and closes it on the
 * way out. It decides no rule: which figure wins, which sound plays, what
 * "paused" means — all of that is the shared client's, so the website and the
 * app cannot drift (memory: feedback_no_mechanical_divergence). The bidder
 * app wraps the same feed in its own hook.
 *
 * A sale on the Supabase book (or a source not resolved yet) has no feed:
 * `feed` is null and the page keeps Realtime (`useLotSubscription`).
 *
 * A page READS the feed during render (`feed.lot(id)`, `feed.you(id)`,
 * `feed.status()`); `version` is what makes it render again. Renders are
 * coalesced to at most one per `RENDER_EVERY_MS` (the first at once, the last
 * always), so a house watching a 535-lot sale at its busiest re-draws its
 * table a few times a second, not once per bid. Sounds and the ticker listen
 * to the feed directly (`useFeedEvent`), so they are never delayed by this.
 */
export interface PageFeedOptions {
  /** Lots on screen now, top first (`useOnScreenLots`). */
  view?: readonly string[] | undefined;
  /** Lots the viewer watches in this sale. */
  watch?: readonly string[] | undefined;
  /** The sale-wide bid ticker (the Lots page). */
  ticker?: boolean | undefined;
  /** One summary of every lot on open and after a reconnect (list pages). */
  summary?: boolean | undefined;
  /** The sale's state as the page knows it (`feedSaleStateFromAuctionStatus`). */
  initialSaleState?: FeedSaleState | null | undefined;
}

export interface PageFeed {
  /** The engine feed; null on the Supabase book or before the source is known. */
  feed: SaleFeed | null;
  /** The feed's connection status; null with no feed. */
  status: SaleFeedStatusInfo | null;
  /** Changes whenever the feed's lots, standings, status or sale state do. */
  version: number;
}

/** At most one page render per this many ms from feed changes (first at once, last always). */
export const RENDER_EVERY_MS = 100;

interface VersionStore {
  subscribe: (onChange: () => void) => () => void;
  getSnapshot: () => number;
  dispose: () => void;
}

/** A version counter over a feed's changes, with coalesced notifications. */
function versionStore(feed: SaleFeed): VersionStore {
  let version = 0;
  let lastNotify = Number.NEGATIVE_INFINITY;
  let trailing: ReturnType<typeof setTimeout> | null = null;
  const listeners = new Set<() => void>();

  const notify = () => {
    lastNotify = Date.now();
    for (const listener of [...listeners]) listener();
  };
  const bump = () => {
    version += 1;
    if (trailing !== null) return;
    const wait = lastNotify + RENDER_EVERY_MS - Date.now();
    if (wait <= 0) {
      notify();
      return;
    }
    trailing = setTimeout(() => {
      trailing = null;
      notify();
    }, wait);
  };
  const offs = [
    feed.on('lots', bump),
    feed.on('you', bump),
    feed.on('status', bump),
    feed.on('sale', bump),
  ];
  return {
    subscribe: (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    getSnapshot: () => version,
    dispose: () => {
      for (const off of offs) off();
      if (trailing !== null) clearTimeout(trailing);
      trailing = null;
      listeners.clear();
    },
  };
}

const NO_STORE: Pick<VersionStore, 'subscribe' | 'getSnapshot'> = {
  subscribe: () => () => {},
  getSnapshot: () => 0,
};

/** A stable string for the set, so a re-render with the same lots sends nothing. */
const keyOf = (ids: readonly string[] | undefined): string => (ids ?? []).join('\u0001');

export function useSaleFeed(source: LiveSource | null, options: PageFeedOptions = {}): PageFeed {
  const { session, loading: sessionLoading } = useSession();
  const userId = session?.user.id ?? null;
  // The feed and its version store are made, and unmade, together in one
  // effect — never in render, where a discarded render would leak listeners.
  const [live, setLive] = useState<{ feed: SaleFeed; store: VersionStore } | null>(null);
  const feed = live?.feed ?? null;

  // The interest at the moment a feed is created rides in its `subscribe`;
  // changes after that go through `setView` / `setWatch` below.
  const latest = useRef(options);
  latest.current = options;

  const engine = source?.kind === 'engine' ? source : null;
  const engineUrl = engine?.engineUrl ?? null;
  const engineAuctionId = engine?.engineAuctionId ?? null;
  const houseId = engine?.houseId ?? null;
  const ticker = options.ticker === true;
  const summary = options.summary === true;

  useEffect(() => {
    // Not before the session is known: a signed-in bidder's feed would open
    // once as a signed-out observer (a whole summary for nothing) and then
    // again with their token.
    if (sessionLoading || engineUrl === null || engineAuctionId === null || houseId === null) {
      setLive(null);
      return;
    }
    const opts = latest.current;
    const created = createSaleFeed({
      engineUrl,
      engineAuctionId,
      houseId,
      getToken: currentAccessToken,
      view: opts.view,
      watch: opts.watch,
      ticker,
      summary,
      initialSaleState: opts.initialSaleState ?? null,
    });
    const store = versionStore(created);
    setLive({ feed: created, store });
    return () => {
      store.dispose();
      created.close();
    };
    // A new viewer is a new socket: the engine binds the role on connect.
  }, [engineUrl, engineAuctionId, houseId, ticker, summary, userId, sessionLoading]);

  const viewKey = keyOf(options.view);
  const watchKey = keyOf(options.watch);
  useEffect(() => {
    feed?.setView(latest.current.view ?? []);
  }, [feed, viewKey]);
  useEffect(() => {
    feed?.setWatch(latest.current.watch ?? []);
  }, [feed, watchKey]);

  const active = live?.store ?? NO_STORE;
  const version = useSyncExternalStore(active.subscribe, active.getSnapshot, active.getSnapshot);

  return { feed, status: feed === null ? null : feed.status(), version };
}

/**
 * Listen to one feed event with the latest handler, for the things that must
 * not wait for a render: sounds, the ticker, the bid history.
 */
export function useFeedEvent<K extends keyof SaleFeedEvents>(
  feed: Pick<SaleFeed, 'on'> | null,
  event: K,
  handler: SaleFeedEvents[K],
): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (feed === null) return;
    const relay = ((...args: Parameters<SaleFeedEvents[K]>) => {
      (ref.current as (...a: Parameters<SaleFeedEvents[K]>) => void)(...args);
    }) as SaleFeedEvents[K];
    return feed.on(event, relay);
  }, [feed, event]);
}
